import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
import { insuranceClaimsCommandReasonSchema, insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, insuranceClaimsMoneyCentsSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
export const insuranceClaimsPaymentAdministrationCapability = 'insurance-claims.payment-administration.v1';
export const insuranceClaimsPaymentAdministrationVersionsCapability = 'insurance-claims.payment-administration-versions.v1';
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
export const insuranceClaimsPaymentAdministrationStateSchema = z.enum(['active', 'closed']);
export const insuranceClaimsPaymentMilestoneKeySchema = z.enum([
    'deductible', 'acv', 'rcv', 'recoverable-depreciation', 'mortgage-check',
]);
export const insuranceClaimsPaymentMilestoneStatusSchema = z.enum([
    'unknown', 'not-applicable', 'pending', 'documented', 'collected', 'received', 'requested',
    'not-ready', 'denied', 'endorsed', 'deposited', 'released', 'reconciled', 'blocked',
]);
export const insuranceClaimsPaymentEvidenceKindSchema = z.enum([
    'system-initialized', 'operator-attested', 'claim-snapshot', 'carrier-review',
]);
export const insuranceClaimsPaymentEvidenceSchema = z.strictObject({
    kind: insuranceClaimsPaymentEvidenceKindSchema,
    resourceId: insuranceClaimsIdSchema.nullable(),
    versionId: insuranceClaimsIdSchema.nullable(),
    contentHash: sha256.nullable(),
    observedAt: z.string().datetime({ offset: true }),
    note: nullableText(2_000),
}).superRefine((value, context) => {
    if (value.kind === 'system-initialized') {
        if (value.resourceId !== null || value.versionId !== null || value.contentHash !== null || value.note !== null) {
            context.addIssue({ code: 'custom', path: ['kind'], message: 'System initialization carries no asserted source.' });
        }
        return;
    }
    if (value.kind === 'operator-attested') {
        if (value.resourceId !== null || value.versionId !== null || value.contentHash !== null ||
            value.note === null || value.note.length < 10) {
            context.addIssue({ code: 'custom', path: ['note'], message: 'Operator evidence requires an explanation and no provider identity.' });
        }
        return;
    }
    if (value.resourceId === null || value.versionId === null || value.contentHash === null) {
        context.addIssue({ code: 'custom', path: ['resourceId'], message: 'Versioned source evidence requires exact resource identity.' });
    }
});
const statusesByMilestone = {
    deductible: new Set(['unknown', 'not-applicable', 'pending', 'documented', 'collected', 'reconciled']),
    acv: new Set(['unknown', 'not-applicable', 'pending', 'received', 'reconciled']),
    rcv: new Set(['unknown', 'not-applicable', 'pending', 'requested', 'received', 'reconciled']),
    'recoverable-depreciation': new Set(['unknown', 'not-applicable', 'not-ready', 'requested', 'received', 'denied', 'reconciled']),
    'mortgage-check': new Set(['unknown', 'not-applicable', 'pending', 'endorsed', 'deposited', 'released', 'reconciled', 'blocked']),
};
const insuranceClaimsPaymentMilestoneShape = {
    key: insuranceClaimsPaymentMilestoneKeySchema,
    status: insuranceClaimsPaymentMilestoneStatusSchema,
    amountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    evidence: insuranceClaimsPaymentEvidenceSchema,
    note: nullableText(2_000),
    updatedAt: z.string().datetime({ offset: true }),
    updatedByMembershipId: insuranceClaimsIdSchema,
};
export const insuranceClaimsPaymentMilestoneSchema = z.strictObject(insuranceClaimsPaymentMilestoneShape).superRefine((value, context) => {
    if (!statusesByMilestone[value.key].has(value.status)) {
        context.addIssue({ code: 'custom', path: ['status'], message: 'Milestone status is invalid for this payment milestone.' });
    }
    if (value.key === 'mortgage-check' && value.amountCents !== null) {
        context.addIssue({ code: 'custom', path: ['amountCents'], message: 'Mortgage-check administration does not carry a payment amount.' });
    }
});
export const insuranceClaimsComplianceReviewReferenceSchema = z.strictObject({
    reviewId: insuranceClaimsIdSchema,
    versionId: insuranceClaimsIdSchema,
    contentHash: sha256,
    jurisdictionState: z.string().regex(/^[A-Z]{2}$/),
    approvedAt: z.string().datetime({ offset: true }),
});
export const insuranceClaimsPaymentAdministrationDataSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    complianceReview: insuranceClaimsComplianceReviewReferenceSchema.nullable(),
    milestones: z.array(insuranceClaimsPaymentMilestoneSchema).length(5),
}).superRefine((value, context) => {
    const keys = new Set(value.milestones.map(({ key }) => key));
    if (keys.size !== 5) {
        context.addIssue({ code: 'custom', path: ['milestones'], message: 'Every payment milestone must appear exactly once.' });
    }
});
export const insuranceClaimsPaymentAdministrationVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsPaymentAdministrationVersionsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    administrationId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    revision: z.number().int().positive(),
    state: insuranceClaimsPaymentAdministrationStateSchema,
    commandKind: z.enum(['create', 'update-milestone', 'set-compliance-review', 'close', 'reopen']),
    contentHash: sha256,
    reason: insuranceClaimsCommandReasonSchema,
    actorMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsPaymentAdministrationDataSchema,
});
export const insuranceClaimsPaymentAdministrationSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsPaymentAdministrationCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    state: insuranceClaimsPaymentAdministrationStateSchema,
    revision: z.number().int().positive(),
    currentVersionId: insuranceClaimsIdSchema,
    createdByMembershipId: insuranceClaimsIdSchema,
    updatedByMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsPaymentAdministrationVersionSchema,
});
export const insuranceClaimsPaymentMilestoneEvidenceInputSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('operator-attested'), observedAt: z.string().datetime({ offset: true }), note: z.string().trim().min(10).max(2_000) }),
    z.strictObject({ kind: z.literal('claim-snapshot') }),
    z.strictObject({ kind: z.literal('carrier-review'), reviewId: insuranceClaimsIdSchema }),
]);
export const insuranceClaimsCreatePaymentAdministrationCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: insuranceClaimsIdSchema,
    administrationId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    complianceReviewId: insuranceClaimsIdSchema.nullable().default(null),
    reason: insuranceClaimsCommandReasonSchema,
});
const paymentCommandBase = {
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: insuranceClaimsCommandReasonSchema,
};
export const insuranceClaimsUpdatePaymentMilestoneCommandSchema = z.strictObject({
    kind: z.literal('update-milestone'),
    ...paymentCommandBase,
    milestoneKey: insuranceClaimsPaymentMilestoneKeySchema,
    status: insuranceClaimsPaymentMilestoneStatusSchema,
    amountCents: insuranceClaimsMoneyCentsSchema.nullable().default(null),
    evidence: insuranceClaimsPaymentMilestoneEvidenceInputSchema,
    note: nullableText(2_000).default(null),
});
export const insuranceClaimsSetPaymentComplianceReviewCommandSchema = z.strictObject({
    kind: z.literal('set-compliance-review'),
    ...paymentCommandBase,
    complianceReviewId: insuranceClaimsIdSchema.nullable(),
});
export const insuranceClaimsPaymentAdministrationStateCommandSchema = z.strictObject({
    kind: z.enum(['close', 'reopen']),
    ...paymentCommandBase,
});
export const insuranceClaimsPaymentAdministrationCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsUpdatePaymentMilestoneCommandSchema,
    insuranceClaimsSetPaymentComplianceReviewCommandSchema,
    insuranceClaimsPaymentAdministrationStateCommandSchema,
]);
export const insuranceClaimsPaymentMilestoneToneSchema = z.enum(['neutral', 'attention', 'risk', 'good']);
export const insuranceClaimsPaymentMilestoneActionSchema = z.enum([
    'confirm-status', 'document-deductible', 'confirm-collection', 'confirm-acv-receipt',
    'reconcile-acv', 'request-rcv', 'reconcile-rcv', 'prepare-depreciation-evidence',
    'follow-up-depreciation', 'review-depreciation-denial', 'track-mortgage-administration',
    'escalate-mortgage-blocker', 'reconcile-mortgage-release', 'none',
]);
export const insuranceClaimsPaymentMilestoneProjectionSchema = z.strictObject({
    ...insuranceClaimsPaymentMilestoneShape,
    tone: insuranceClaimsPaymentMilestoneToneSchema,
    needsAttention: z.boolean(),
    complete: z.boolean(),
    action: insuranceClaimsPaymentMilestoneActionSchema,
});
export const insuranceClaimsPaymentAdministrationProjectionSchema = z.strictObject({
    data: insuranceClaimsPaymentAdministrationSchema,
    milestones: z.array(insuranceClaimsPaymentMilestoneProjectionSchema).length(5),
    summary: z.strictObject({
        attentionCount: z.number().int().nonnegative(),
        blockedCount: z.number().int().nonnegative(),
        completeCount: z.number().int().nonnegative(),
        readyForCloseout: z.boolean(),
        nextAction: insuranceClaimsPaymentMilestoneActionSchema,
    }),
});
export const insuranceClaimsPaymentAdministrationListQuerySchema = z.strictObject({
    claimId: insuranceClaimsIdSchema.nullable().default(null),
    projectId: insuranceClaimsIdSchema.nullable().default(null),
    state: insuranceClaimsPaymentAdministrationStateSchema.nullable().default(null),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsPaymentAdministrationResponseSchema = z.strictObject({
    data: insuranceClaimsPaymentAdministrationSchema,
    projection: insuranceClaimsPaymentAdministrationProjectionSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsPaymentAdministrationsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsPaymentAdministrationProjectionSchema),
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsPaymentAdministrationVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsPaymentAdministrationVersionSchema),
});
//# sourceMappingURL=insurance-claims-payment-administration.contract.js.map